import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link,useNavigate } from 'react-router-dom';
import NewconfirmationModal from '../shared/NewconfirmationModal';
import AllFailureModal from '../shared/AllFailureModal';
import ConfirmationModal from '../shared/ConfirmationModal';
import loaderimage from '../../images/giphy.gif';

type Notice = {
  notice_id: number;
  job_id: number;
  title: string;
  description: string;
  audience: string;
  posted_by: string;
  posted_on: string;
  expiry_date: string;
};

const NoticeList = () => {
  const [allNotice, setAllNotice] = useState<Notice[]>([]);
  const [modalShow, setModalShow] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [failureModalShow, setFailureModalShow] = useState(false);
  const [failureMessage, setFailureMessage] = useState("");
  const [modalMessage, setModalMessage] = useState('');
  const [pendingAction, setPendingAction] = useState<{ type: 'delete' | 'edit', id?: number, data?: Notice } | null>(null);
  const [loader, setLoader] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filteredNotice, setFilteredNotice] = useState<Notice[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalNotices, setTotalNotices] = useState(0);
  const [pageSize, setPageSize] = useState(100);


  const fetchNoticeList = async (page=1) => {
    try {
      setLoader(true);
      setError(null);
      const url = `${process.env.REACT_APP_API_URL}notice/allnotice?page=${page}&limit=1000`;
      const response = await axios.get(url);
      setAllNotice(response.data.notices || []);
      setFilteredNotice(response.data.notices || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalNotices(response.data.totalNotices || 0);
      setCurrentPage(response.data.currentPage || page);
    } catch (error) {
      console.log('Error fetching Notice list:', error);
      setFailureMessage('Failed to fetch notices. Please try again later.');
      setFailureModalShow(true);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchNoticeList(currentPage);
  }, [currentPage]);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      if (searchTerm === '') {
        setFilteredNotice(allNotice);
      } else {
        const filtered = allNotice.filter((notice) =>
          notice.title.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredNotice(filtered);
      }
    }, 300);
    return () => clearTimeout(debounceTimeout);
  }, [searchTerm, allNotice]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleDeleteNotice = (id: number) => {
    setPendingAction({ type: 'delete', id });
    setModalMessage("Are you sure you want to delete this notice?");
    setModalShow(true);
  };

  const handleEditNotice = (notice: Notice) => {
    setPendingAction({ type: 'edit', data: notice });
    setModalMessage("Are you sure you want to save changes to this notice?");
    setModalShow(true);
  };

  const handleConfirmation = async (isConfirmed: boolean) => {
    setModalShow(false);
    if (isConfirmed && pendingAction) {
      if (pendingAction.type === 'delete' && pendingAction.id) {
        try {
          const url = `${process.env.REACT_APP_API_URL}notice/deletenotice/${pendingAction.id}`;
          await axios.put(url);
          setSuccessMessage('Notice deleted successfully!');
          setShowSuccessModal(true);
          setTimeout(() => {
            setShowSuccessModal(false);
            fetchNoticeList(currentPage);
          }, 3000);
        } catch (error) {
          console.log('Error deleting Notice:', error);
          setFailureMessage('Failed to delete notice. Please try again later.');
          setFailureModalShow(true);
        }
      } else if (pendingAction.type === 'edit' && pendingAction.data) {
        try {
          const url = `${process.env.REACT_APP_API_URL}notice/updatenotice/${pendingAction.data.notice_id}`;
          await axios.put(url, pendingAction.data);
          fetchNoticeList(currentPage);
        } catch (error) {
          console.log('Error updating Notice:', error);
          setFailureMessage('Failed to update notice. Please try again later.');
          setFailureModalShow(true);
        }
      }
    }
  };

  /*const deleteNotice = async (id: any) => {
    if (window.confirm("Are you sure you want to delete this Notice ?")) {
      try {
        setLoader(true);
        const url = `${process.env.REACT_APP_API_URL}notice/deletenotice/${id}`;
        await axios.put(url);

        toast.success("Notice Deleted");
        fetchNoticeList();
      } catch (error) {
        console.error('Error deleting Notice:', error);
        toast.error("Error deleting notice");
      } finally {
        setLoader(false);
      }
      setPendingAction(null);
    }
  };*/


  const isExpired = (expiryDate: string) => {
    const currentDate = new Date();
    const noticeExpiryDate = new Date(expiryDate);
    return noticeExpiryDate < currentDate;
  };
  const handlePageChange = (page: number) => {
    fetchNoticeList(page);
  };
  const startEntry = (currentPage - 1) * 100 + 1;
  const endEntry = Math.min(currentPage * 100, totalNotices);

  return (
    <>
      <div className="">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="text-black fw-bold">Notice Management</h2>
          <div className="d-flex gap-2 align-items-center" style={{ width: "510px" }}>
            <span className="fw-bold text-secondary text-nowrap">{filteredNotice.length} results</span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by Title"
              value={searchTerm}
              onChange={handleSearch}
            />
            <Link to="/noticeadd" className="btn bg-info text-white">
              + Create Notice
            </Link>
          </div>
        </div>
        <div className="card shadow-sm border-0 rounded">
          <div className="card-header bg-info border-bottom py-3">
            <h5 className="mb-0 text-white">Notice List</h5>
          </div>
          <div className="card-body p-0">
              <div className="table-responsive" style={{ maxHeight: "500px", overflowY: "auto" }}>
                <table className="table table-hover align-middle mb-0">
                  <thead className="bg-light text-center">
                    <tr>
                      <th>Sr No.</th>
                      <th>Notice ID</th>
                      <th>Title</th>
                      <th>JobID</th>
                      <th>Posted By</th>
                      <th>Description</th>
                      <th>Posted On</th>
                      <th>Audience</th>
                      <th>Expiry Date</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                  {loader ? (
                  <tr>
                    <td colSpan={9} className="text-center py-2">
                      <img src={loaderimage} alt="Loading..." height="40" />
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={9} className="text-center text-danger fw-bold py-3">
                      <div className="alert alert-danger text-center m-3" role="alert">
                        Oops! Something went wrong. Please try again later.
                      </div>
                    </td>
                  </tr>
            ) : filteredNotice.length > 0 ? (
                    filteredNotice.map((notice, index) => (
                      <tr key={notice.notice_id} className="border-bottom text-center">
                        <td>{index + 1}</td>
                        <td>{notice.notice_id}</td>
                        <td>{notice.title}</td>
                        <td>{notice.job_id}</td>
                        <td>{notice.posted_by}</td>
                        <td>{notice.description}</td>
                        <td>{new Date(notice.posted_on).toDateString()}</td>
                        <td>{notice.audience}</td>
                        <td>
                         {isExpired(notice.expiry_date) ? (
                           <span className="text-danger fw-bold">
                          { new Date(notice.expiry_date).toDateString()}
                           </span>
                          ) : (
                          <span>{new Date(notice.expiry_date).toDateString()}</span>
                          )}
                        </td>
              <td className="text-center">
              <div className="d-flex justify-content-center gap-2">
                <Link to={`/noticeupdate/${notice.notice_id}`} className="btn btn-sm btn-outline-warning" onClick={() => handleEditNotice(notice)}>Edit</Link>
             
                <button onClick={() => handleDeleteNotice(notice.notice_id)} className="btn btn-sm btn-outline-danger">Delete</button>
                </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={12} className="text-center text-muted fw-bold">
                        <div className="alert alert-secondary text-center m-3" role="alert">
                          No Notices Available
                        </div>
                      </td>
                    </tr>
                  )}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
           <div className="position-sticky bottom-0 bg-white py-2 border-top">
          <div className="d-flex justify-content-between align-items-center">
            <div className="text-muted ps-3">
            Showing {startEntry}-{endEntry} of {totalNotices} entries
                  </div>
                  <nav>
                    <ul className="pagination mb-0 pe-3">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>
                      {[...Array(totalPages)].map((_, index) => (
                        <li 
                          key={index} 
                          className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                        >
                          <button 
                            className="page-link" 
                            onClick={() => handlePageChange(index + 1)}
                          >
                            {index + 1}
                          </button>
                        </li>
                      ))}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                  </div>
                  </div>
          </div>
        </div>
        <ConfirmationModal
        show={modalShow}
        modalMessage={modalMessage}
        onHide={() => setModalShow(false)}
        confirmation={handleConfirmation}
        operationType={pendingAction?.type === 'delete' ? "Delete" : "Save"}
        />

        <NewconfirmationModal
        show={showSuccessModal}
        modalMessage={successMessage}
        onHide={() => setShowSuccessModal(false)}
        confirmation={handleConfirmation}
        operationType={"OK"}
        />
      <AllFailureModal
      show={failureModalShow}
      onHide={() => setFailureModalShow(false)}
      showRetry={true}
      onRetry={fetchNoticeList}
      />
      </div>
    </>
  );
};

export default NoticeList; 